import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, Table, Tag, Button, Space, Modal, message, Switch, Tooltip } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import { SelectC, InputC } from '@/i2antdV5'
import QueryManager from '@/utils/queryManager'
import ApiPollingManager from '@/utils/ApiPollingManager'
import {
  fetchDispatchingRules,
  deleteDispatchingRuleAction,
  toggleDispatchingRuleAction
} from '@/redux/order/dispatcherSlice'
import { renderTextStr } from '@/utils/renderUtils'
import DispatchingRuleModal from './share/DispatchingRuleModal'
import './DispatcherList.less'

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '订单管理', href: '/client/order/orderList' },
  { breadcrumbName: '派单管理' }
]

// 服务类型配置
const SERVICE_TYPE_CONFIG = {
  maternity: { text: '产康', color: 'purple' },
  nursing: { text: '护理', color: 'cyan' },
  home: { text: '到家', color: 'geekblue' }
}

// 获取默认查询参数
const getDefaultQuery = () => {
  return {
    page: 1,
    pageSize: 20
  }
}

const defaultQuery = getDefaultQuery()

/**
 * 接口配置管理
 */
const getAPIConfig = (componentInstance) => ({
  // 只需加载一次的接口配置
  once: [],
  // 实时获取接口配置
  realtime: [],
  // 定时获取接口配置
  polling: [],
  // 手动触发接口
  manual: [
    {
      name: 'fetchDispatchingRules',
      dispatch: fetchDispatchingRules,
      getParams: (query) => ({ query: { ...query } })
    }
  ]
})

class DispatcherList extends React.Component {
  constructor(props) {
    super(props)
    this.lastUpdateTime = 0
    this.lastUpdateKey = null
    
    // 初始化QueryManager
    this.queryManager = new QueryManager(defaultQuery, {
      syncToUrl: true
    })
    
    // 创建 API 轮询管理器
    this.apiManager = new ApiPollingManager(getAPIConfig(this), this)
    
    // 设置更新回调
    this.queryManager.setUpdateCallback((query) => {
      this.apiManager.fetchData(query, true)
    })
    
    // 设置navigate函数
    if (props.navigate) {
      this.queryManager.setNavigate(props.navigate)
    }

  }

  componentDidMount() {
    const { location } = this.props
    const initializedQuery = this.queryManager.initialize(location)
    
    // 重写 fetchData 方法
    const originalFetchData = this.apiManager.fetchData.bind(this.apiManager)
    this.apiManager.fetchData = async (value, showErrorDialog = true) => {
      if (value) {
        const oldCallback = this.queryManager.onUpdateCallback
        this.queryManager.setUpdateCallback(null)
        this.queryManager.updateQuery(value, undefined, { 
          autoFetch: false,
          syncToUrl: this.queryManager.syncToUrl
        })
        this.queryManager.setUpdateCallback(oldCallback)
      }
      const currentQuery = this.queryManager.getQuery()
      this.apiManager.currentQuery = currentQuery
      return originalFetchData(currentQuery, showErrorDialog)
    }
    
    // 初始化管理器
    this.apiManager.currentQuery = initializedQuery
    this.apiManager.init(initializedQuery)
  }

  componentDidUpdate(prevProps) {
    this.apiManager.checkReduxErrors(prevProps, () => ({
      fetchDispatchingRules: this.props.dispatcherObj?.list
    }))
  }

  componentWillUnmount() {
    this.apiManager.cleanup()
  }

  /**
   * 更新query参数
   */
  updateQuery = (keyOrUpdates, value, options = {}) => {
    const isActionCall = typeof keyOrUpdates === 'object' && keyOrUpdates !== null && options.syncToUrl === undefined
    
    const now = Date.now()
    let updateKey = null
    
    if (typeof keyOrUpdates === 'string') {
      updateKey = `${keyOrUpdates}:${JSON.stringify(value)}`
    } else if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const keys = Object.keys(keyOrUpdates).sort()
      updateKey = keys.map(k => `${k}:${JSON.stringify(keyOrUpdates[k])}`).join('|')
    }
    
    if (updateKey && 
        this.lastUpdateKey === updateKey && 
        now - this.lastUpdateTime < 300) {
      return this.queryManager.getQuery()
    }
    
    this.lastUpdateTime = now
    this.lastUpdateKey = updateKey
    
    if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const currentQuery = this.queryManager.getQuery()
      let hasChange = false
      for (const key in keyOrUpdates) {
        if (currentQuery[key] !== keyOrUpdates[key]) {
          hasChange = true
          break
        }
      }
      if (!hasChange) {
        return currentQuery
      }
    } else if (typeof keyOrUpdates === 'string') {
      const currentQuery = this.queryManager.getQuery()
      if (currentQuery[keyOrUpdates] === value) {
        return currentQuery
      }
    }
    
    const finalOptions = {
      autoFetch: true,
      syncToUrl: options.syncToUrl !== undefined 
        ? options.syncToUrl 
        : (isActionCall ? false : this.queryManager.syncToUrl),
      ...options
    }
    return this.queryManager.updateQuery(keyOrUpdates, value, finalOptions)
  }

  /**
   * 获取当前query参数
   */
  getQuery = () => {
    return this.queryManager.getQuery()
  }

  /**
   * 获取数据
   */
  fetchData = async (value, showErrorDialog = true) => {
    return this.apiManager.fetchData(value, showErrorDialog)
  }

  /**
   * 分页切换
   */
  handlePageChange = (page, pageSize) => {
    this.updateQuery({ page, pageSize })
  }

  /**
   * 保存规则成功回调
   */
  handleSaveSuccess = () => {
    // 刷新列表
    const currentQuery = this.getQuery()
    this.fetchData(currentQuery)
  }

  /**
   * 删除规则
   */
  handleDeleteRule = (rule) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除规则"${rule.ruleName}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const { dispatch } = this.props
          await dispatch(deleteDispatchingRuleAction(rule.id)).unwrap()
          message.success('删除成功')
          // 刷新列表
          const currentQuery = this.getQuery()
          this.fetchData(currentQuery)
        } catch (error) {
          message.error(error || '删除失败')
        }
      }
    })
  }

  /**
   * 切换启用/禁用状态
   */
  handleToggleEnabled = async (rule, enabled) => {
    try {
      const { dispatch } = this.props
      await dispatch(toggleDispatchingRuleAction({ 
        ruleId: rule.id, 
        enabled 
      })).unwrap()
      message.success(enabled ? '已启用' : '已禁用')
    } catch (error) {
      message.error(error || '操作失败')
    }
  }

  /**
   * 获取表格列配置
   */
  getColumns = () => {
    return [
      {
        title: (
          <span>
            规则名称
            <Tooltip title="派单规则的名称，用于标识和区分不同的派单策略">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'ruleName',
        key: 'ruleName',
        width: 200,
        fixed: 'left',
        render: (text) => renderTextStr(text)
      },
      {
        title: (
          <span>
            服务类型
            <Tooltip title="该规则适用的服务类型，包括产康、护理、到家等服务">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'serviceType',
        key: 'serviceType',
        width: 100,
        render: (serviceType) => {
          const config = SERVICE_TYPE_CONFIG[serviceType] || {}
          return <Tag color={config.color}>{config.text || serviceType}</Tag>
        }
      },
      {
        title: (
          <span>
            搜索半径
            <Tooltip title="系统搜索接单人员的范围半径，单位为公里。在此范围内的服务人员才会被匹配">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'radius',
        key: 'radius',
        width: 100,
        render: (radius) => radius ? `${radius}公里` : '-'
      },
      {
        title: (
          <span>
            最低评分
            <Tooltip title="接单人员的最低评分要求，只有评分不低于此值的人员才会被匹配">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: ['matchConditions', 'minRating'],
        key: 'minRating',
        width: 100,
        render: (minRating) => minRating ? minRating.toFixed(1) : '-'
      },
      {
        title: (
          <span>
            推送数量
            <Tooltip title="同时推送给多少个服务人员，系统会按照匹配条件筛选出最合适的人员进行推送">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: ['pushSettings', 'pushCount'],
        key: 'pushCount',
        width: 100,
        render: (pushCount) => pushCount || '-'
      },
      {
        title: (
          <span>
            超时时间
            <Tooltip title="推送后等待接单的超时时间，单位为分钟。超过此时间无人接单，系统将自动重新派单或使用其他策略">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: ['pushSettings', 'timeout'],
        key: 'timeout',
        width: 100,
        render: (timeout) => timeout ? `${timeout}分钟` : '-'
      },
      {
        title: (
          <span>
            包含即将空闲
            <Tooltip title="是否包含即将空闲的服务人员。开启后，系统会考虑即将完成当前服务的员工，提前进行派单">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: ['matchConditions', 'includeUpcoming'],
        key: 'includeUpcoming',
        width: 120,
        render: (includeUpcoming) => includeUpcoming ? '是' : '否'
      },
      {
        title: (
          <span>
            启用状态
            <Tooltip title="控制该派单规则是否生效。禁用后，该规则将不会参与自动派单">
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'enabled',
        key: 'enabled',
        width: 100,
        render: (enabled, record) => (
          <Switch
            checked={enabled}
            onChange={(checked) => this.handleToggleEnabled(record, checked)}
            checkedChildren="启用"
            unCheckedChildren="禁用"
          />
        )
      },
      {
        title: '操作',
        key: 'action',
        width: 180,
        fixed: 'right',
        render: (_, record) => {
          return (
            <Space size="small">
              <DispatchingRuleModal
                baseProps={{
                  editingRule: record,
                  onSuccess: this.handleSaveSuccess
                }}
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                >
                  编辑
                </Button>
              </DispatchingRuleModal>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => this.handleDeleteRule(record)}
              >
                删除
              </Button>
            </Space>
          )
        }
      }
    ]
  }

  render() {
    const { dispatcherObj } = this.props
    const { list: ruleListData } = dispatcherObj || {}
    const { fetching, data, query } = ruleListData || {}
    
    const currentQuery = this.getQuery()
    const { list = [], total = 0, pageNumber = 1, pageSize = 20 } = data || {}
    
    // 服务类型选项
    const serviceTypeOptions = [
      { id: 'maternity', value: '产康' },
      { id: 'nursing', value: '护理' },
      { id: 'home', value: '到家' }
    ]
    
    // 启用状态选项
    const enabledOptions = [
      { id: true, value: '已启用' },
      { id: false, value: '已禁用' }
    ]

    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
        />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <InputC
                  label="规则名称"
                  query={currentQuery}
                  searchKey="ruleName"
                  action={this.updateQuery}
                  placeholder="请输入规则名称"
                />
                <SelectC
                  label="服务类型"
                  query={currentQuery}
                  searchKey="serviceType"
                  options={serviceTypeOptions}
                  action={this.updateQuery}
                  placeholder="服务类型"
                />
                <SelectC
                  label="启用状态"
                  query={currentQuery}
                  searchKey="enabled"
                  options={enabledOptions}
                  action={this.updateQuery}
                  placeholder="启用状态"
                />
              </div>
              <div className="toolbar-actions">
                <DispatchingRuleModal
                  baseProps={{
                    editingRule: null,
                    onSuccess: this.handleSaveSuccess
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    新建规则
                  </Button>
                </DispatchingRuleModal>
              </div>
            </section>
            <Table
              loading={fetching}
              columns={this.getColumns()}
              dataSource={list}
              rowKey="id"
              scroll={{ x: 1200 }}
              size="small"
              pagination={{
                current: pageNumber,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: this.handlePageChange,
                onShowSizeChange: (current, size) => this.handlePageChange(1, size)
              }}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const DispatcherListWrapper = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  
  const dispatcherState = useSelector(state => state.order?.dispatcher || {})
  
  const props = {
    dispatch,
    location,
    navigate,
    dispatcherObj: dispatcherState
  }
  
  return <DispatcherList {...props} />
}

export default DispatcherListWrapper

